import React, { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogPortal, DialogOverlay, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";

// Local, close-button-less DialogContent so the forced-password-change modal
// does not display an inert 'X' affordance.
const CloselessDialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
CloselessDialogContent.displayName = "CloselessDialogContent";

export default function ChangePasswordModal({ open }) {
  const { refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 10) return toast.error("New password must be at least 10 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. You're all set.");
      await refresh();
    } catch (err) {
      toast.error(errText(err, "Could not update password"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open}>
      <CloselessDialogContent
        data-testid="change-password-modal"
        className="sm:max-w-md rounded-2xl bg-white border border-[#E5E4DE]"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-indigo-700">
            <Lock className="w-5 h-5" />
            <DialogTitle className="text-lg font-bold text-slate-900">Set your password</DialogTitle>
          </div>
          <DialogDescription className="text-slate-500">
            For security, choose a new password before continuing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-medium text-slate-600">Current password</label>
            <Input type="password" required value={current}
                   onChange={(e) => setCurrent(e.target.value)}
                   data-testid="cp-current"
                   className="mt-1 h-11" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">New password (min 10 chars)</label>
            <Input type="password" required value={next}
                   onChange={(e) => setNext(e.target.value)}
                   data-testid="cp-new"
                   className="mt-1 h-11" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Confirm new password</label>
            <Input type="password" required value={confirm}
                   onChange={(e) => setConfirm(e.target.value)}
                   data-testid="cp-confirm"
                   className="mt-1 h-11" />
          </div>
          <Button type="submit" disabled={submitting}
                  data-testid="cp-submit"
                  className="w-full h-11 bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl">
            {submitting ? "Updating..." : "Update password"}
          </Button>
        </form>
      </CloselessDialogContent>
    </Dialog>
  );
}
